import { useMemo } from "react";
import { Outlet } from "react-router-dom";
import AppShell from "@/components/shell/app-shell";
import { ADMIN_PORTAL_NAV_SECTIONS, type NavSection } from "@/lib/constants";
import { usePermission } from "@/hooks/use-permission";
import { useAccessRequestOpenCount } from "@/hooks/use-access-request-count";

const ACCESS_REQUESTS_HREF = "/admin/access-requests";

/**
 * The admin nav with the "Access requests" entry resolved for this session: left out for a
 * session without admin:users:read, and badged with the open count otherwise (the sidebar hides
 * a badge of 0). Specs/access-requests S-13, ALL-5723.
 */
export function adminNavSections(canReadAccessRequests: boolean, openCount: number): NavSection[] {
  return ADMIN_PORTAL_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items
      .filter((item) => item.href !== ACCESS_REQUESTS_HREF || canReadAccessRequests)
      .map((item) => (item.href === ACCESS_REQUESTS_HREF && openCount > 0 ? { ...item, badge: openCount } : item)),
  })).filter((section) => section.items.length > 0);
}

// This layout used to derive a global `apiConnected` flag from a single
// probe request (`GET /v1/admin/roles`) and show `MockDataBanner`
// unconditionally whenever that call didn't return 2xx. That conflated a
// permission-denied 403 with "mock data is on screen" and showed the
// banner on every admin page regardless of what that page actually
// rendered — the shared-wrapper gap that fooled the Users page (see
// context/agent_playbooks/page-delivery-resources.md §5.4 gotcha #1). The
// banner is not a layout default: see hooks/use-fixture-banner.tsx for the
// page-owned replacement.
export function AdminLayout() {
  const canReadAccessRequests = usePermission("user:read");
  const openCount = useAccessRequestOpenCount();
  const navSections = useMemo(
    () => adminNavSections(canReadAccessRequests, openCount),
    [canReadAccessRequests, openCount],
  );
  return (
    <AppShell navSections={navSections}>
      <Outlet />
    </AppShell>
  );
}
